import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, Users, Building2, CalendarCheck, CalendarOff,
  ListTodo, Trophy, ClipboardList, Megaphone, X, BarChart3, ScrollText,
  ShieldCheck, ChevronRight, Briefcase, FileText, Camera, Target, FileBarChart, Shirt, Phone, Database, MessageSquareWarning, Wallet, UserPlus, Tag, Car, FileImage
} from 'lucide-react';

const NAV_CONFIG = [
  {
    type: 'link',
    path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard,
    roles: ['super_admin', 'management', 'branch_manager', 'employee']
  },
  {
    type: 'link',
    path: '/monthly-summary', label: 'Monthly Summary', icon: FileBarChart,
    roles: ['super_admin', 'management', 'branch_manager']
  },
  {
    type: 'group',
    key: 'daily-ops',
    label: 'Daily Ops',
    icon: ShieldCheck,
    roles: ['super_admin', 'management', 'branch_manager', 'employee'],
    children: [
      { path: '/attendance', label: 'Attendance', icon: CalendarCheck, roles: ['super_admin', 'management', 'branch_manager', 'employee'] },
      { path: '/achievements', label: 'Brand Achievements', icon: Trophy, roles: ['super_admin', 'management', 'branch_manager', 'employee'] },
      { path: '/konattu-achievements', label: 'Konattu Achievements', icon: Target, roles: ['super_admin', 'management', 'branch_manager', 'employee'] },
      { path: '/compliance', label: 'Compliance', icon: ShieldCheck, roles: ['super_admin', 'management', 'branch_manager'] },
      { path: '/meeting-photos', label: 'Meeting Photos', icon: Camera, roles: ['super_admin', 'management', 'branch_manager'] },
    ]
  },
  {
    type: 'group',
    key: 'people',
    label: 'People',
    icon: Users,
    roles: ['super_admin', 'management', 'branch_manager'],
    children: [
      { path: '/employees', label: 'Employees', icon: Users, roles: ['super_admin', 'management', 'branch_manager'] },
      { path: '/recruitment', label: 'Recruitment', icon: UserPlus, roles: ['super_admin', 'management'] },
      { path: '/data-bank', label: 'Data Bank', icon: Database, roles: ['super_admin', 'management'] },
      { path: '/branches', label: 'Branches', icon: Building2, roles: ['super_admin', 'management'] },
      { path: '/brands', label: 'Brands', icon: Tag, roles: ['super_admin'] },
    ]
  },
  {
    type: 'group',
    key: 'workflow',
    label: 'Workflow',
    icon: Briefcase,
    roles: ['super_admin', 'management', 'branch_manager', 'employee'],
    children: [
      { path: '/leave', label: 'Leave', icon: CalendarOff, roles: ['super_admin', 'management', 'branch_manager', 'employee'] },
      { path: '/leave-details', label: 'Leave Details', icon: CalendarOff, roles: ['super_admin', 'management', 'branch_manager'] },
      { path: '/tasks', label: 'Tasks', icon: ListTodo, roles: ['super_admin', 'management', 'branch_manager', 'employee'] },
      { path: '/requests', label: 'Requests', icon: Shirt, roles: ['super_admin', 'management', 'branch_manager', 'employee'] },
      { path: '/complaints', label: 'Complaints', icon: MessageSquareWarning, roles: ['super_admin', 'management', 'branch_manager', 'employee'] },
      { path: '/payroll', label: 'Payroll', icon: Wallet, roles: ['super_admin', 'management'] },
      { path: '/travel-allowance', label: 'Travel Allowance', icon: Car, roles: ['super_admin', 'management', 'branch_manager'] },
      { path: '/brand-payslips', label: 'Payslip Uploads', icon: FileImage, roles: ['super_admin', 'management', 'branch_manager', 'employee'] },
    ]
  },
  {
    type: 'group',
    key: 'reports',
    label: 'Reports',
    icon: FileText,
    roles: ['super_admin', 'management', 'branch_manager', 'employee'],
    children: [
      { path: '/analytics', label: 'Analytics', icon: BarChart3, roles: ['super_admin', 'management', 'branch_manager'] },
      { path: '/photo-monitoring', label: 'Photo Monitoring', icon: Camera, roles: ['super_admin', 'management', 'branch_manager'] },
      { path: '/tests', label: 'Knowledge Tests', icon: ClipboardList, roles: ['super_admin', 'management', 'branch_manager', 'employee'] },
      { path: '/audit-log', label: 'Audit Log', icon: ScrollText, roles: ['super_admin', 'management'] },
    ]
  },
  {
    type: 'link',
    path: '/announcements', label: 'Announcements', icon: Megaphone,
    roles: ['super_admin', 'management', 'branch_manager', 'employee']
  },
];

/* ───── Shared nav link class (dark sidebar theme) ───── */
const navItemClass = (isActive, isChild = false) => {
  const base = `relative flex items-center gap-3 rounded-md font-medium transition-all duration-150 group`;
  const padding = isChild ? 'px-3 py-2 text-[13px]' : 'px-3 py-2.5 text-sm';
  const state = isActive
    ? 'bg-red-600 text-white shadow-[0_4px_14px_-4px_rgba(220,38,38,0.5)]'
    : 'text-neutral-100 hover:text-white hover:bg-white/5';
  return `${base} ${padding} ${state}`;
};

function SidebarLink({ item, onClose }) {
  return (
    <NavLink
      to={item.path}
      onClick={onClose}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
      className={({ isActive }) => navItemClass(isActive)}
    >
      {({ isActive }) => (
        <>
          {isActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-white rounded-r-full" />}
          <item.icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-white' : 'text-neutral-300 group-hover:text-white'}`} />
          <span className="flex-1">{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

function SidebarGroup({ group, onClose, userRole }) {
  const location = useLocation();
  const children = group.children.filter(c => c.roles.includes(userRole));
  const isAnyChildActive = children.some(c => location.pathname.startsWith(c.path));
  const [open, setOpen] = useState(isAnyChildActive);
  useEffect(() => { if (isAnyChildActive) setOpen(true); }, [isAnyChildActive]);

  if (children.length === 0) return null;

  return (
    <div data-testid={`nav-group-${group.key}`}>
      <button
        data-testid={`nav-group-toggle-${group.key}`} aria-expanded={open} aria-controls={`nav-items-${group.key}`}
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-150 group
          ${isAnyChildActive
            ? 'text-white bg-white/5'
            : 'text-neutral-200 hover:text-white hover:bg-white/5'}`}
      >
        <span className="flex items-center gap-3">
          <group.icon className={`h-4 w-4 flex-shrink-0 ${isAnyChildActive ? 'text-red-400' : 'text-neutral-300 group-hover:text-white'}`} />
          {group.label}
        </span>
        <ChevronRight className={`h-3.5 w-3.5 text-neutral-400 transition-transform duration-200 ${open ? 'rotate-90' : ''}`} />
      </button>
      <div id={`nav-items-${group.key}`} hidden={!open} className="mt-1">
        <div className="pl-3 ml-3 border-l border-white/10 space-y-0.5">
          {children.map(child => (
            <NavLink
              key={child.path}
              to={child.path}
              onClick={onClose}
              data-testid={`nav-${child.label.toLowerCase().replace(/\s/g, '-')}`}
              className={({ isActive }) => navItemClass(isActive, true)}
            >
              {({ isActive }) => (
                <>
                  <child.icon className={`h-3.5 w-3.5 flex-shrink-0 ${isActive ? 'text-white' : 'text-neutral-300 group-hover:text-white'}`} />
                  <span>{child.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Sidebar({ onClose }) {
  const { user } = useAuth();
  const role = user?.system_role;

  return (
    <div className="h-full min-h-0 bg-gradient-to-b from-neutral-950 to-black text-white flex flex-col w-full border-r border-white/5" data-testid="sidebar">
      {/* Logo */}
      <div className="h-16 shrink-0 flex items-center justify-between px-5 border-b border-white/5 bg-black/40">
        <img
          src="/assets/konattu-logo.png"
          alt="Konattu HRMS"
          className="h-11 w-auto object-contain"
          data-testid="sidebar-logo"
        />
        <button onClick={onClose} className="xl:hidden p-1.5 hover:bg-white/10 rounded-md flex items-center justify-center" data-testid="close-sidebar-button" aria-label="Close navigation">
          <X className="h-4 w-4 text-neutral-300" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 min-h-0 py-4 px-3 space-y-1 overflow-y-auto overscroll-contain" aria-label="Main navigation">
        {NAV_CONFIG.filter(item => item.roles.includes(role)).map((item) =>
          item.type === 'link' ? (
            <SidebarLink key={item.path} item={item} onClose={onClose} />
          ) : (
            <SidebarGroup key={item.key} group={item} onClose={onClose} userRole={role} />
          )
        )}
      </nav>

      {/* Contact HR + User info */}
      <div className="border-t border-white/5 bg-black/30">
        <a href="https://wa.me/919645044111" target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors border-b border-white/5 group"
          data-testid="contact-hr-button">
          <div className="h-8 w-8 rounded-md bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center group-hover:bg-emerald-500/25 transition-colors">
            <Phone className="h-3.5 w-3.5 text-emerald-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-white">Contact HR</p>
            <p className="text-[11px] text-emerald-300/70">+91 9645044111</p>
          </div>
        </a>
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center ring-2 ring-white/10 flex-shrink-0">
            <span className="text-white text-sm font-semibold">{user?.name?.[0]?.toUpperCase() || '?'}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate" data-testid="sidebar-user-name">{user?.name}</p>
            <p className="text-[11px] text-neutral-300 capitalize truncate">{user?.system_role?.replace('_', ' ')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
